import type React from 'react';

import { Image, Text } from '@change/ds-core-ui';
import { SignaturesIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useGetStaticAssetUrl } from '@change-corgi/core-react/assets';
import { Translate, useI18n } from '@change-corgi/core-react/i18n';

type Props = {
	/** `petition.photo.petitionLarge.url`; the default petition image stands in when absent. */
	photoUrl: string | null | undefined;
	ask: string;
	className?: string;
};

/**
 * Post-sign petition header for the video-first arms: thumbnail, "You just signed", petition title.
 *
 * Prop-driven rather than context-reading because it renders on two pages - `actions` and
 * `petitionVideo` - which have different page contexts.
 */
export const JustSignedBar: React.FC<Props> = ({ photoUrl, ask, className }) => {
	const { translate } = useI18n();
	const getStaticAssetUrl = useGetStaticAssetUrl();

	const imageUrl = photoUrl || getStaticAssetUrl('/images/default-petition-darker.gif');

	return (
		<div
			className={cn('border-stroke-weaker flex items-center gap-2 border-0 border-b border-solid px-4 py-2', className)}
		>
			<Image
				data-testid="just-signed-thumbnail"
				src={imageUrl}
				alt={translate('corgi.petition_actions.image.alt_text')}
				className="h-10 w-10 shrink-0 rounded-md object-cover"
			/>
			{/* min-w-0 lets the title actually truncate instead of forcing the row wider */}
			<div className="flex min-w-0 flex-col">
				<div className="flex items-center gap-1">
					<SignaturesIcon size={16} className="text-icon-strong" />
					<Text variant="body" size="x-small" fontWeight="bold" className="text-typography-strong">
						<Translate value="corgi.petition_actions.just_signed_bar.label" />
					</Text>
				</div>
				<Text variant="body" size="small" truncate className="text-typography-strong">
					{ask}
				</Text>
			</div>
		</div>
	);
};
